import {useState} from "react";
import {sized} from "@/lib/utils";
import {Search, MapPin, SlidersHorizontal, Check, ChevronRight, Heart, Plus, FileSearch, Sparkles, PhoneCall} from "lucide-react";
import BusinessModal from "@/pages/BusinessModal";
import SuggestBrandModal from "@/pages/modals/SuggestBrand";
import WishlistModal from "@/pages/modals/Wishlist";
import PurchaseReportModal from "@/pages/modals/PurchaseReport";
import {MARKET_CATEGORIES} from "@/lib/constants";
import {apiPost} from "@/lib/api";
import {useSite, Rich} from "@/lib/site";

const slug = (x) => x.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and");

export default function Bazaar({listings, notify, basket, toggleBasket, wishlist, toggleWishlist, requireAuth, setActive, focus}) {
  const [tab, setTab] = useState("browse");
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(() => listings.find(l => l.listing_id === focus) || null);
  const [subModal, setSubModal] = useState(null);
  const [pdrProduct, setPdrProduct] = useState(null);
  const t = useSite();

  const cats = ["All", ...MARKET_CATEGORIES];
  const shown = listings.filter(x => {
    const catOk = filter === "All" || x.category === filter;
    const qOk = !q || x.name.toLowerCase().includes(q.toLowerCase()) || x.description.toLowerCase().includes(q.toLowerCase());
    const whereOk = !where || x.location.toLowerCase().includes(where.toLowerCase());
    return catOk && qOk && whereOk;
  });

  return (
    <div className="classified">
      <div className="classified-top">
        <div>
          <div className="eyebrow">BAZAAR · AUTHENTIC PRODUCTS</div>
          <h1><Rich text={t("bazaar_title")}/></h1>
          <p>{t("bazaar_sub")}</p>
        </div>
        <div className="market-top-actions">
          <button data-testid="wishlist-pill" className="basket-pill ghost" onClick={() => setSubModal("wishlist")}>
            <Heart size={16}/><span>Wishlist</span><b>{wishlist.length}</b>
          </button>
        </div>
      </div>


      <div className="activity-tabs" data-testid="market-tabs">
        <button data-testid="market-tab-browse" className={tab === "browse" ? "activity-tab active" : "activity-tab"} onClick={() => setTab("browse")}>Browse products</button>
        <button data-testid="market-tab-find" className={tab === "find" ? "activity-tab active" : "activity-tab"} onClick={() => setTab("find")}>Find a product for me</button>
      </div>

      {tab === "find" ? <FindBrand notify={notify} requireAuth={requireAuth}/> : (
        <>
          <div className="directory-search">
            <div><Search size={18}/><input data-testid="marketplace-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Patola, kundan, khakhra..."/></div>
            <div><MapPin size={18}/><input data-testid="marketplace-location" value={where} onChange={e => setWhere(e.target.value)} placeholder="Location"/></div>
            <button data-testid="marketplace-search-button" className="coral-button" onClick={() => notify(`${shown.length} products found`)}>Search</button>
          </div>
          <div className="directory-shortcuts">
            <span>Popular:</span>
            {["Patola", "Kundan", "Kutch", "Khakhra"].map(x => (
              <button data-testid={`popular-${x}`} key={x} onClick={() => setQ(x)}>{x}</button>
            ))}
            <button data-testid="suggest-brand" className="suggest-link" onClick={() => setSubModal("suggest")}><Plus size={13}/> Suggest a product</button>
          </div>

          <div className="marketplace-body">
            <aside className="directory-filters">
              <div className="filter-title"><strong>Browse by category</strong><SlidersHorizontal size={16}/></div>
              <select data-testid="market-category-select" className="mobile-cat-select" value={filter} onChange={e => setFilter(e.target.value)}>
                {cats.map(x => <option key={x}>{x}</option>)}
              </select>
              {cats.map(x => (
                <button data-testid={`market-category-${slug(x)}`} className={filter === x ? "directory-filter active" : "directory-filter"} onClick={() => setFilter(x)} key={x}>
                  {x}<ChevronRight size={14}/>
                </button>
              ))}
            </aside>

            <section className="directory-results">
              <div className="results-toolbar">
                <strong data-testid="market-results-count">{shown.length} products in {filter}</strong>
              </div>
              {shown.map(x => (
                <article className="directory-card" key={x.listing_id} data-testid={`listing-${x.listing_id}`}>
                  <img onClick={() => setActive("product", x.listing_id)} src={sized(x.images?.[0] || x.image, 420)} alt={x.name} loading="lazy" decoding="async"/>
                  <div className="directory-card-main">
                    <div className="listing-meta"><span>{x.category}</span><span className="verified"><Check size={12}/> VERIFIED</span>{x.images?.length > 1 && <span>{x.images.length} photos</span>}</div>
                    <button data-testid={`business-page-${x.listing_id}`} className="business-title" onClick={() => setActive("product", x.listing_id)}>{x.name}</button>
                    {x.brand && x.brand !== x.name && <small className="product-brand">by {x.brand}</small>}
                    <div className="rating-line"><strong>★ {x.rating || "New"}</strong><span>{x.reviews || 0} reviews</span>{x.price && <><span>·</span><b className="product-price" data-testid={`listing-price-${x.listing_id}`}>{x.price}</b></>}</div>
                    <p>{x.description}</p>
                    <div className="directory-actions">
                      <button data-testid={`business-wishlist-${x.listing_id}`} onClick={() => { toggleWishlist(x.listing_id); notify(wishlist.includes(x.listing_id) ? "Removed from wishlist" : `${x.name} added to wishlist`); }}>
                        <Heart size={15} fill={wishlist.includes(x.listing_id) ? "currentColor" : "none"}/> {wishlist.includes(x.listing_id) ? "Wishlisted" : "Wishlist"}
                      </button>
                      <button data-testid={`business-pdr-${x.listing_id}`} className="interest-button" onClick={() => setPdrProduct(x.name)}>
                        <FileSearch size={15}/> Purchase Decision Report
                      </button>
                    </div>
                  </div>
                </article>
              ))}
              {!shown.length && (
                <div className="empty-directory" data-testid="market-category-empty">
                  <Sparkles size={25}/>
                  <h3>Coming super soon</h3>
                  <p>We are evaluating authentic products for {filter}. Want something specific right now?</p>
                  <button data-testid="empty-find-brand" className="coral-button" onClick={() => setTab("find")}>Find a product for me</button>
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {selected && <BusinessModal business={selected} close={() => setSelected(null)} notify={notify} requireAuth={requireAuth} onReport={() => { setPdrProduct(selected.name); setSelected(null); }} onGetFromIndia={() => { setSelected(null); setActive("gfi"); }}/>}
      {pdrProduct !== null && <PurchaseReportModal product={pdrProduct} close={() => setPdrProduct(null)} notify={notify} requireAuth={requireAuth}/>}
            {subModal === "wishlist" && <WishlistModal close={() => setSubModal(null)} listings={listings} wishlist={wishlist} toggleWishlist={toggleWishlist} notify={notify}/>}
      {subModal === "suggest" && <SuggestBrandModal close={() => setSubModal(null)} notify={notify}/>}
    </div>
  );
}

function FindBrand({notify, requireAuth}) {
  const empty = {looking_for: "", category: MARKET_CATEGORIES[0], other_category: "", brand_link: "", ship_to: "", budget: "", phone: ""};
  const [f, setF] = useState(empty);
  const ch = e => setF({...f, [e.target.name]: e.target.value});
  const submit = (e) => {
    e.preventDefault();
    requireAuth(async () => {
      try { await apiPost("/brand-requests", f); setF(empty); notify("Got it! Your concierge will call you back with options"); }
      catch { notify("Please check the form and try again"); }
    });
  };
  return (
    <section className="find-brand" data-testid="find-brand-panel">
      <div>
        <div className="eyebrow">FIND A PRODUCT FOR ME</div>
        <h2>Tell us what you love. <em>We&rsquo;ll find the best one.</em></h2>
        <p>Describe the product, style or quality you want. We shortlist trusted makers, evaluate them, and call you back with options.</p>
      </div>
      <form onSubmit={submit} className="concierge-form" data-testid="find-brand-form">
        <label>What are you looking for?
          <textarea data-testid="find-brand-what" required minLength={3} name="looking_for" value={f.looking_for} onChange={ch} placeholder="e.g. Handloom cotton kurtas for men, earthy colours, under 5,000 rupees each"/>
        </label>
        <div className="form-row">
          <label>Category
            <select data-testid="find-brand-category" name="category" value={f.category} onChange={ch}>
              {MARKET_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              <option>Other</option>
            </select>
          </label>
          {f.category === "Other" && (
            <label>Your category
              <input data-testid="find-brand-other-category" required name="other_category" value={f.other_category} onChange={ch} placeholder="e.g. Musical instruments"/>
            </label>
          )}
          <label>Ship to
            <input data-testid="find-brand-shipto" name="ship_to" value={f.ship_to} onChange={ch} placeholder="Toronto, Canada"/>
          </label>
        </div>
        <label>Share the link of the brand if you know it (optional)
          <input data-testid="find-brand-link" type="url" name="brand_link" value={f.brand_link} onChange={ch} placeholder="https://..."/>
        </label>
        <label>Phone / WhatsApp
          <input data-testid="find-brand-phone" required minLength={6} name="phone" value={f.phone} onChange={ch} placeholder="+1 416..."/>
        </label>
        <button data-testid="find-brand-submit" className="coral-button full" type="submit"><PhoneCall size={15}/> Arrange a call back</button>
      </form>
    </section>
  );
}
